import { ShieldOff } from "lucide-react";
import type { ReactNode } from "react";

import { useSession } from "@/api/queries/auth";
import { EmptyState, PageLoader } from "@/components/ui";
import { useTenant } from "@/hooks/useTenant";

import { canOpenPlatformSection, type PlatformSectionKey } from "./navigation";

function NoAccess({ description }: { description: string }) {
  return (
    <div className="p-6">
      <EmptyState icon={<ShieldOff />} title="You don't have access to this area" description={description} />
    </div>
  );
}

/**
 * Friendly state for areas the member can't use (the API independently returns 403). `code` may list
 * several capabilities: any one of them opens the page (`all` requires every one).
 */
export function RequirePermission({ code, all = false, children }: { code: string | string[]; all?: boolean; children: ReactNode }) {
  const { can } = useTenant();
  const codes = Array.isArray(code) ? code : [code];
  if (all ? codes.every((c) => can(c)) : codes.some((c) => can(c))) return <>{children}</>;
  return <NoAccess description="Ask a tenant administrator if you need it." />;
}

/** The platform counterpart: the section's capability comes from the navigation map (the API returns 403 too). */
export function RequireCapability({ section, children }: { section: PlatformSectionKey; children: ReactNode }) {
  const { data: session } = useSession();
  if (!session) return <PageLoader />;
  if (canOpenPlatformSection(section, session.platform.capabilities)) return <>{children}</>;
  return <NoAccess description="Ask a Kasten platform administrator if you need it." />;
}
